import { Global, css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Calendar } from 'primereact/calendar';
import { Chips } from 'primereact/chips';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import React from 'react';

const filterVarsCss = css`
    :root {
        --filter-chip-bg: #e8f0fc;
        --filter-chip-border: rgba(31, 91, 214, 0.25);
        --filter-chip-text: #1f5bd6;
        --filter-picker-row-hover: #f5f8fd;
        --filter-picker-row-selected: #e8f0fc;
        --filter-category-label: #6b7a92;
        --filter-divider: rgba(10, 30, 58, 0.06);
        --filter-chip-stagger: 40ms;
    }

    /*
     * PrimeReact Dropdown/MultiSelect panels render into a portal outside the
     * drawer, so drawer-scoped emotion styles don't reach their items. Bump
     * the option rows + filter input + empty-message to the drawer's 14px.
     */
    .p-dropdown-panel .p-dropdown-items .p-dropdown-item,
    .p-dropdown-panel .p-dropdown-empty-message,
    .p-dropdown-panel .p-dropdown-filter,
    .p-multiselect-panel .p-multiselect-items .p-multiselect-item,
    .p-multiselect-panel .p-multiselect-empty-message,
    .p-multiselect-panel .p-multiselect-filter,
    .p-multiselect-panel .p-multiselect-header .p-multiselect-filter-container .p-inputtext {
        font-size: 14px;
    }

    /*
     * PrimeReact Calendar panel also portals outside the drawer. Bring the
     * month/year header, weekday row, and day cells up to 14px so the date
     * picker is legible instead of the cramped ~10-11px PrimeReact default.
     */
    .p-datepicker {
        font-size: 14px;
    }
    .p-datepicker .p-datepicker-header .p-datepicker-title,
    .p-datepicker .p-datepicker-header .p-datepicker-title .p-datepicker-year,
    .p-datepicker .p-datepicker-header .p-datepicker-title .p-datepicker-month,
    .p-datepicker table th,
    .p-datepicker table td,
    .p-datepicker table td > span {
        font-size: 14px;
    }
    .p-datepicker table td > span {
        width: 2.25rem;
        height: 2.25rem;
    }
    /* Extra horizontal room per cell so the two-month view doesn't feel squished. */
    .p-datepicker table th,
    .p-datepicker table td {
        padding: 0.25rem 1.5rem;
    }
    /*
     * Reserve space for 6 week rows in every month so the panel height doesn't
     * jump when navigating between 4/5/6-week months. Leaves trailing whitespace
     * on shorter months instead of faded next-month days.
     * Each row ≈ 2.25rem cell + 0.5rem padding = 2.75rem; 6 rows = 16.5rem.
     */
    .p-datepicker .p-datepicker-calendar-container {
        min-height: 16.5rem;
    }
    /*
     * Selected / in-range days: use a saturated chip so the day number stays
     * legible, instead of PrimeReact's pale default tint that blends in.
     */
    .p-datepicker table td > span.p-highlight {
        background: var(--filter-chip-text) !important;
        color: #ffffff !important;
        font-weight: 600;
    }
    .p-datepicker table td > span.p-highlight:hover {
        background: #1a4fbb !important;
    }
`;

export const FilterDrawerGlobalStyles: React.FC = () => React.createElement(Global, { styles: filterVarsCss });

export const chipFadeIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0);    }
`;

// ---------------------------------------------------------------------------
// Chip components
// ---------------------------------------------------------------------------

export const ChipListStacked = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;

    /* Stretch stacked chips to full width and push the × to the far right. */
    & > span[role='listitem'] {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;

export const ChipListInline = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
`;

export const AppliedChip = styled.span`
    background: var(--filter-chip-bg);
    border: 1px solid var(--filter-chip-border);
    color: var(--filter-chip-text);
    border-radius: 16px;
    padding: 4px 10px 4px 12px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    animation: ${chipFadeIn} 200ms ease both;
    animation-delay: calc(var(--chip-index, 0) * var(--filter-chip-stagger));

    /* Push the × button to the trailing edge of the chip. */
    & > button {
        margin-left: auto;
    }
`;

export const AppliedChipRemoveButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    color: inherit;
    opacity: 0.7;

    &:hover {
        opacity: 1;
    }
`;

export const OverflowChip = styled.span`
    background: rgba(31, 91, 214, 0.08);
    border: 1px solid var(--filter-chip-border);
    color: var(--filter-chip-text);
    border-radius: 16px;
    padding: 4px 12px;
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    cursor: pointer;
    animation: ${chipFadeIn} 200ms ease both;
    animation-delay: calc(var(--chip-index, 0) * var(--filter-chip-stagger));
`;

// ---------------------------------------------------------------------------
// Picker-row components
// ---------------------------------------------------------------------------

export const PickerSearchInput = styled.input`
    width: 100%;
    border: 1px solid rgba(10, 30, 58, 0.15);
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;

    &:focus {
        border-color: #1f5bd6;
    }
`;

export const PickerCategoryHeader = styled.div`
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--filter-category-label);
    padding: 10px 16px 4px;
    font-weight: 600;
`;

export const PickerRowLabel = styled.label<{ isSelected?: boolean; isDisabled?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
    background: ${({ isSelected }) => (isSelected ? 'var(--filter-picker-row-selected)' : 'transparent')};

    &:hover {
        background: ${({ isSelected, isDisabled }) =>
            isDisabled
                ? 'transparent'
                : isSelected
                  ? 'var(--filter-picker-row-selected)'
                  : 'var(--filter-picker-row-hover)'};
    }
`;

export const PickerRowTitle = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #0a1e3a;
`;

export const PickerRowDescription = styled.span`
    display: block;
    font-size: 12px;
    color: #6b7a92;
    margin-top: 1px;
`;

// ---------------------------------------------------------------------------
// Configured filter list
// ---------------------------------------------------------------------------

export const DrawerDivider = styled.hr`
    border: none;
    border-top: 1px solid var(--filter-divider);
    margin: 0;
`;

export const ConfiguredFilterList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ConfiguredFilterLabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
`;

export const ConfiguredFilterLabel = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #0a1e3a;
`;

export const ConfiguredFilterRemoveButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: inline-flex;
    align-items: center;
    color: #6b7a92;
    opacity: 0.7;
    line-height: 1;

    &:hover {
        opacity: 1;
        color: #0a1e3a;
    }
`;

// ---------------------------------------------------------------------------
// Input components (drawer-scoped) — bump PrimeReact defaults up to 14px so
// chip + input text matches the picker rows and design-system body type.
// ---------------------------------------------------------------------------

export const DrawerMultiSelect = styled(MultiSelect)`
    width: 100%;
    min-height: 42px;

    .p-multiselect-label {
        font-size: 14px;
        padding: 0.625rem 0.75rem;
    }
`;

export const DrawerDropdown = styled(Dropdown)`
    width: 100%;
    min-height: 42px;

    .p-inputtext {
        font-size: 14px;
        padding: 0.625rem 0.75rem;
    }
`;

// Shared sizing so every drawer input renders at the same height + width.
// 42px matches PrimeReact's default InputText (0.75rem vertical padding +
// 14px content + 2px border).
const drawerInputReset = css`
    width: 100%;
    min-height: 42px;
    box-sizing: border-box;
    font-size: 14px;
`;

export const DrawerInputText = styled(InputText)`
    ${drawerInputReset};
    padding: 0.625rem 0.75rem;
`;

// ---------------------------------------------------------------------------
// FiltersToggle bar (button + inline applied-chips)
// ---------------------------------------------------------------------------

export const FiltersToggleBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 8px 12px;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
`;

export const FiltersToggleButton = styled.button<{ hasActiveFilters?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;

    background: ${({ hasActiveFilters }) => (hasActiveFilters ? '#1f5bd6' : '#ffffff')};
    color: ${({ hasActiveFilters }) => (hasActiveFilters ? '#ffffff' : '#0a1e3a')};
    border: 1px solid
        ${({ hasActiveFilters }) => (hasActiveFilters ? '#1f5bd6' : 'rgba(10, 30, 58, 0.15)')};

    &:hover {
        background: ${({ hasActiveFilters }) => (hasActiveFilters ? '#1a4fbb' : '#f5f8fd')};
    }
`;

export const DrawerCalendar = styled(Calendar)`
    ${drawerInputReset};

    .p-inputtext {
        font-size: 14px;
        padding: 0.625rem 0.75rem;
    }

    .p-button {
        color: #6b7a92;
        background: transparent;
        border-color: rgba(10, 30, 58, 0.15);
        border-left: none;

        &:hover {
            background: #f5f8fd;
            color: #0a1e3a;
        }
    }
`;

export const DrawerChips = styled(Chips)`
    width: 100%;

    &.p-chips .p-chips-multiple-container {
        ${drawerInputReset};
        display: flex;
        align-items: center;
        padding: 0.25rem 0.5rem 0.75rem;
        flex-wrap: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        gap: 4px;
    }

    &.p-chips .p-chips-multiple-container .p-chips-token {
        padding: 0.125rem 0.5rem;
        margin: 0;
    }

    .p-chips-input-token {
        padding: 0;
        margin: 0;
        min-width: 80px;
        flex: 1 1 80px;
    }

    .p-chips-input-token input {
        font-size: 14px;
        height: 100%;
    }

    .p-chips-token-label {
        font-size: 14px;
    }
`;
